import {
  AchievementDefinition,
  AchievementId,
  AchievementSnapshot,
  City,
  Coordinates,
  DailyHistory,
  GameMode,
  RunKind,
  StatsStore,
} from '../types';
import { dailySeed } from './prng';
import { getStorageBackend } from './storage';

const ACHIEVEMENT_KEY = 'cp_achievements_v1';

interface AchievementRecordUpdate {
  unlocks: AchievementDefinition[];
  snapshot: AchievementSnapshot;
}

export interface ChoiceAchievementEvent {
  correct: boolean;
  streak: number;
  winner: City;
  loser: City;
  mode: GameMode;
  runKind: RunKind;
  populationDiff: number;
  closestCityDistanceKm?: number;
  playerLocation?: Coordinates;
}

const ACHIEVEMENTS: AchievementDefinition[] = [
  {
    id: 'first-guess',
    name: 'Figured out how to play',
    description: "You clicked one of them. That’s the whole game.",
    category: 'Onboarding',
    icon: '🟢',
    target: 1,
  },
  {
    id: 'first-mistake',
    name: 'We all make mistakes',
    description: 'It was inevitable.',
    category: 'Onboarding',
    icon: '🟢',
    target: 1,
  },
  {
    id: 'streak-5',
    name: 'You’re getting the hang of this',
    description: 'Not bad. Not bad at all.',
    category: 'Streaks',
    icon: '🟢',
    target: 5,
  },
  {
    id: 'streak-10',
    name: 'You’re quite good',
    description: 'This is starting to look intentional.',
    category: 'Streaks',
    icon: '🔵',
    target: 10,
  },
  {
    id: 'streak-15',
    name: 'That’s insane',
    description: 'Statistically speaking, wow.',
    category: 'Streaks',
    icon: '🔵',
    target: 15,
  },
  {
    id: 'streak-20',
    name: 'How did we get this far?',
    description: 'At some point this stopped being luck.',
    category: 'Streaks',
    icon: '🔵',
    target: 20,
  },
  {
    id: 'streak-50',
    name: 'Ok, you’re clearly cheating',
    description: 'We won’t tell anyone. Probably.',
    category: 'Streaks',
    icon: '🟣',
    target: 50,
  },
  {
    id: 'daily-2',
    name: 'You came back',
    description: 'So it begins.',
    category: 'Daily',
    icon: '🟢',
    target: 2,
  },
  {
    id: 'daily-5',
    name: 'Hope you enjoy',
    description: 'You’re officially a regular.',
    category: 'Daily',
    icon: '🔵',
    target: 5,
  },
  {
    id: 'daily-30',
    name: 'Dedicated',
    description: 'At this point, it’s a routine.',
    category: 'Daily',
    icon: '🟣',
    target: 30,
  },
  {
    id: 'daily-365',
    name: 'Anniversary',
    description: 'A whole year. Impressive and concerning.',
    category: 'Daily',
    icon: '🟡',
    target: 365,
  },
  {
    id: 'daily-3650',
    name: 'I wasn’t planning on supporting this so long',
    description: 'Please stop. Or don’t. I guess.',
    category: 'Daily',
    icon: '🔴',
    target: 3650,
  },
  {
    id: 'damascus',
    name: 'Road to Damascus',
    description: 'A sudden realization.',
    category: 'Easter Eggs',
    icon: '🟣',
  },
  {
    id: 'new-york',
    name: 'Your friendly neighborhood spider',
    description: 'With great population comes great responsibility.',
    category: 'Easter Eggs',
    icon: '🕷️',
  },
  {
    id: 'london',
    name: 'Chewsday, Innit',
    description: "It's tea time",
    category: 'Easter Eggs',
    icon: '🍵',
  },
  {
    id: 'moscow',
    name: 'Meanwhile, In Russia',
    description: 'Tri Poloski',
    category: 'Easter Eggs',
    icon: '🎯',
  },
  {
    id: 'so-close',
    name: 'So Close',
    description: 'That one hurt.',
    category: 'Precision',
    icon: '🎯',
    target: 50_000,
  },
  {
    id: 'blown-out',
    name: 'Blown Out',
    description: 'No comment.',
    category: 'Precision',
    icon: '💥',
    target: 10_000_000,
  },
  {
    id: 'local-close-call',
    name: 'You should know this',
    description: 'Missed a city within 30 km of your saved location.',
    category: 'Local',
    icon: '📍',
  },
  {
    id: 'local-first-win',
    name: 'Neighborhood hero',
    description: 'Win your first round in local mode.',
    category: 'Local',
    icon: '📌',
  },
];

function defaultSnapshot(): AchievementSnapshot {
  return {
    achievements: ACHIEVEMENTS.reduce((acc, achievement) => {
      acc[achievement.id] = {};
      return acc;
    }, {} as Record<AchievementId, { unlockedAt?: string; progress?: number }>),
    metrics: {
      totalGuesses: 0,
      bestStreak: 0,
      bestDailyStreak: 0,
    },
  };
}

function loadSnapshot(): AchievementSnapshot {
  const backend = getStorageBackend();
  const raw = backend.getItem(ACHIEVEMENT_KEY);
  if (!raw) return defaultSnapshot();
  try {
    const parsed = JSON.parse(raw) as AchievementSnapshot;
    const base = defaultSnapshot();
    return {
      achievements: { ...base.achievements, ...(parsed.achievements ?? {}) },
      metrics: {
        totalGuesses: parsed.metrics?.totalGuesses ?? 0,
        bestStreak: parsed.metrics?.bestStreak ?? 0,
        bestDailyStreak: parsed.metrics?.bestDailyStreak ?? 0,
      },
    };
  } catch (error) {
    console.warn('Failed to parse achievements, using defaults', error);
    return defaultSnapshot();
  }
}

function saveSnapshot(snapshot: AchievementSnapshot): void {
  const backend = getStorageBackend();
  backend.setItem(ACHIEVEMENT_KEY, JSON.stringify(snapshot));
}

function unlockableStreakIds(): AchievementId[] {
  return ['streak-5', 'streak-10', 'streak-15', 'streak-20', 'streak-50'];
}

function unlockableDailyStreakIds(): AchievementId[] {
  return ['daily-2', 'daily-5', 'daily-30', 'daily-365', 'daily-3650'];
}

function findDefinition(id: AchievementId): AchievementDefinition {
  const found = ACHIEVEMENTS.find((entry) => entry.id === id);
  if (!found) throw new Error(`Unknown achievement: ${id}`);
  return found;
}

function normalizeProgress(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, value);
}

export function getAchievementDefinitions(): AchievementDefinition[] {
  return ACHIEVEMENTS;
}

export function calculateDailyStreak(history: DailyHistory, today: Date = new Date()): number {
  const todayKey = dailySeed(today).replace('daily-', '');
  let streak = 0;
  let cursor = new Date(`${todayKey}T00:00:00Z`);
  while (streak < 4_000) {
    const key = cursor.toISOString().split('T')[0];
    if (!history[key]) break;
    streak += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return streak;
}

export class AchievementService {
  private snapshot: AchievementSnapshot;

  constructor() {
    this.snapshot = loadSnapshot();
  }

  getSnapshot(): AchievementSnapshot {
    return this.snapshot;
  }

  reset(): AchievementSnapshot {
    this.snapshot = defaultSnapshot();
    saveSnapshot(this.snapshot);
    return this.snapshot;
  }

  /**
   * Resynchronizes progress for streak and daily achievements based on existing
   * stats and history. This allows legacy users to see achievements tracked
   * before the system existed without replaying runs.
   */
  reconcileFromStats(stats: StatsStore, history: DailyHistory, options?: { silent?: boolean }): AchievementRecordUpdate {
    const bestRecordedStreak = Object.values(stats).reduce((best, record) => Math.max(best, record.bestStreak), 0);
    const currentDailyStreak = calculateDailyStreak(history);
    this.snapshot.metrics.bestStreak = Math.max(this.snapshot.metrics.bestStreak, bestRecordedStreak);
    this.snapshot.metrics.bestDailyStreak = Math.max(this.snapshot.metrics.bestDailyStreak, currentDailyStreak);

    const unlocks: AchievementDefinition[] = [];
    unlockableStreakIds().forEach((id) => {
      const definition = findDefinition(id);
      this.setProgress(id, this.snapshot.metrics.bestStreak);
      if (this.snapshot.metrics.bestStreak >= (definition.target ?? Infinity)) {
        const unlocked = this.unlock(id, options?.silent ?? false);
        if (unlocked) unlocks.push(unlocked);
      }
    });

    unlockableDailyStreakIds().forEach((id) => {
      const definition = findDefinition(id);
      this.setProgress(id, this.snapshot.metrics.bestDailyStreak);
      if (this.snapshot.metrics.bestDailyStreak >= (definition.target ?? Infinity)) {
        const unlocked = this.unlock(id, options?.silent ?? false);
        if (unlocked) unlocks.push(unlocked);
      }
    });

    saveSnapshot(this.snapshot);
    return { unlocks, snapshot: this.snapshot };
  }

  recordChoice(event: ChoiceAchievementEvent, options?: { silent?: boolean }): AchievementRecordUpdate {
    this.snapshot.metrics.totalGuesses += 1;
    this.snapshot.metrics.bestStreak = Math.max(this.snapshot.metrics.bestStreak, event.streak);

    unlockableStreakIds().forEach((id) => this.setProgress(id, this.snapshot.metrics.bestStreak));

    const unlocks: AchievementDefinition[] = [];
    const silent = options?.silent ?? false;

    const firstGuessUnlocked = this.unlock('first-guess', silent);
    if (firstGuessUnlocked) unlocks.push(firstGuessUnlocked);

    if (!event.correct) {
      const mistakeUnlocked = this.unlock('first-mistake', silent);
      if (mistakeUnlocked) unlocks.push(mistakeUnlocked);
      if (event.populationDiff < 50_000) {
        const closeUnlocked = this.unlock('so-close', silent);
        if (closeUnlocked) unlocks.push(closeUnlocked);
      }
      if (event.populationDiff > 10_000_000) {
        const blowoutUnlocked = this.unlock('blown-out', silent);
        if (blowoutUnlocked) unlocks.push(blowoutUnlocked);
      }
    }

    unlockableStreakIds().forEach((id) => {
      const definition = findDefinition(id);
      if (event.streak >= (definition.target ?? Infinity)) {
        const unlocked = this.unlock(id, silent);
        if (unlocked) unlocks.push(unlocked);
      }
    });

    const cityNames = [event.winner.name.toLowerCase(), event.loser.name.toLowerCase()];
    if (event.correct) {
      if (cityNames.some((name) => name.includes('damascus'))) {
        const unlocked = this.unlock('damascus', silent);
        if (unlocked) unlocks.push(unlocked);
      }
      if (cityNames.some((name) => name.includes('new york'))) {
        const unlocked = this.unlock('new-york', silent);
        if (unlocked) unlocks.push(unlocked);
      }
      if (cityNames.some((name) => name.includes('london'))) {
        const unlocked = this.unlock('london', silent);
        if (unlocked) unlocks.push(unlocked);
      }
      if (cityNames.some((name) => name.includes('moscow'))) {
        const unlocked = this.unlock('moscow', silent);
        if (unlocked) unlocks.push(unlocked);
      }
    }

    if (event.runKind === 'local' || event.mode === 'local') {
      if (event.correct && event.streak >= 1) {
        const unlocked = this.unlock('local-first-win', silent);
        if (unlocked) unlocks.push(unlocked);
      }
      if (!event.correct && typeof event.closestCityDistanceKm === 'number' && event.closestCityDistanceKm < 30) {
        const unlocked = this.unlock('local-close-call', silent);
        if (unlocked) unlocks.push(unlocked);
      }
    }

    saveSnapshot(this.snapshot);
    return { unlocks, snapshot: this.snapshot };
  }

  recordDailyHistory(history: DailyHistory, options?: { silent?: boolean }): AchievementRecordUpdate {
    const streak = calculateDailyStreak(history);
    this.snapshot.metrics.bestDailyStreak = Math.max(this.snapshot.metrics.bestDailyStreak, streak);
    unlockableDailyStreakIds().forEach((id) => this.setProgress(id, this.snapshot.metrics.bestDailyStreak));

    const unlocks: AchievementDefinition[] = [];
    const silent = options?.silent ?? false;
    unlockableDailyStreakIds().forEach((id) => {
      const definition = findDefinition(id);
      if (streak >= (definition.target ?? Infinity)) {
        const unlocked = this.unlock(id, silent);
        if (unlocked) unlocks.push(unlocked);
      }
    });

    saveSnapshot(this.snapshot);
    return { unlocks, snapshot: this.snapshot };
  }

  private unlock(id: AchievementId, silent: boolean): AchievementDefinition | null {
    const record = this.snapshot.achievements[id];
    if (record?.unlockedAt) return null;
    this.snapshot.achievements[id] = { ...record, unlockedAt: new Date().toISOString() };
    saveSnapshot(this.snapshot);
    return silent ? null : findDefinition(id);
  }

  private setProgress(id: AchievementId, progressValue: number): void {
    const safe = normalizeProgress(progressValue);
    const record = this.snapshot.achievements[id] ?? {};
    const current = normalizeProgress(record.progress);
    if (safe <= current) return;
    this.snapshot.achievements[id] = { ...record, progress: safe };
  }
}

export function getAchievementStatus(
  definition: AchievementDefinition,
  snapshot: AchievementSnapshot,
): { unlocked: boolean; progress: number; unlockedAt?: string; target?: number } {
  const record = snapshot.achievements[definition.id];
  const unlocked = Boolean(record?.unlockedAt);
  const progress = normalizeProgress(record?.progress ?? (unlocked ? definition.target ?? 0 : 0));
  return {
    unlocked,
    progress,
    unlockedAt: record?.unlockedAt,
    target: definition.target,
  };
}
